import { Location, Orientation } from 'src/api'

export type MovementSeverity = 'slight' | 'minor' | 'moderate' | 'major' | 'critical'
export type MovementAxis = 'motionInX' | 'motionInY' | 'tiltZ'

// Circular delta is capped at half the circle: the largest possible orientation change is 180 degrees
export const MAX_ANGULAR_DELTA = 180

export interface MovementSeverityBand {
  severity: MovementSeverity
  label: string
  description: string
  from: number
  to: number
}

export const movementSeverityBands: MovementSeverityBand[] = [
  { severity: 'slight', label: 'Slight', description: 'nudged / vibration', from: 0, to: 15 },
  { severity: 'minor', label: 'Minor', description: 'small shift', from: 15, to: 45 },
  { severity: 'moderate', label: 'Moderate', description: 'noticeably tilted', from: 45, to: 90 },
  { severity: 'major', label: 'Major', description: 'tipped onto its side', from: 90, to: 135 },
  { severity: 'critical', label: 'Critical', description: 'flipped / inverted', from: 135, to: MAX_ANGULAR_DELTA }
]

export const classifyMovementSeverity = (delta: number): MovementSeverity => {
  const band = movementSeverityBands.find(({ to }) => delta < to) || movementSeverityBands[movementSeverityBands.length - 1]
  return band.severity
}

export interface MovementDetectionResult {
  id: string
  time: string
  deltaX: number
  deltaY: number
  deltaTiltZ: number
  maxDelta: number
  exceededAxes: MovementAxis[]
  severity: MovementSeverity
}

// Shortest angular distance between two readings on the 0-360 degree circle,
// so a change from 1 to 359 degrees is 2 degrees, not 358
export const circularDelta = (a: number, b: number): number => {
  const diff = Math.abs(a - b) % 360
  return Math.min(diff, 360 - diff)
}

export type LocationMovementSeverity = 'minor' | 'moderate' | 'major'

export interface LocationSeverityBand {
  severity: LocationMovementSeverity
  label: string
  from: number
  to: number
}

export const locationSeverityBands: LocationSeverityBand[] = [
  { severity: 'minor', label: 'Minor', from: 0, to: 50 },
  { severity: 'moderate', label: 'Moderate', from: 50, to: 500 },
  { severity: 'major', label: 'Major', from: 500, to: Infinity }
]

// Spec: "up to 50 m" is minor, "50-500" moderate, ">500" major — upper bounds inclusive
export const classifyLocationMovementSeverity = (meters: number): LocationMovementSeverity => {
  if (meters <= 50) return 'minor'
  if (meters <= 500) return 'moderate'
  return 'major'
}

const EARTH_RADIUS_METERS = 6371000
const toRadians = (degrees: number): number => (degrees * Math.PI) / 180

export const haversineDistanceMeters = (a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }): number => {
  const dLat = toRadians(b.latitude - a.latitude)
  const dLng = toRadians(b.longitude - a.longitude)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(a.latitude)) * Math.cos(toRadians(b.latitude)) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h))
}

export interface LocationMovementResult {
  id: string
  time: string
  distance: number
  severity: LocationMovementSeverity
  latitude: number
  longitude: number
  prevLatitude: number
  prevLongitude: number
}

export const detectLocationMovements = (locations: Location[], thresholdMeters: number): LocationMovementResult[] => {
  if (thresholdMeters <= 0) return []

  const sorted = [...locations].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
  const results: LocationMovementResult[] = []

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]
    const curr = sorted[i]
    const distance = haversineDistanceMeters(prev, curr)
    if (distance < thresholdMeters) continue

    results.push({
      id: curr.id,
      time: curr.time,
      distance,
      severity: classifyLocationMovementSeverity(distance),
      latitude: curr.latitude,
      longitude: curr.longitude,
      prevLatitude: prev.latitude,
      prevLongitude: prev.longitude
    })
  }

  return results
}

export const detectSensorMovements = (readings: Orientation[], threshold: number): MovementDetectionResult[] => {
  if (threshold <= 0) return []

  const sorted = [...readings].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
  const results: MovementDetectionResult[] = []

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]
    const curr = sorted[i]
    const deltas: Record<MovementAxis, number> = {
      motionInX: circularDelta(Number(prev.motionInX), Number(curr.motionInX)),
      motionInY: circularDelta(Number(prev.motionInY), Number(curr.motionInY)),
      tiltZ: circularDelta(Number(prev.tiltZ), Number(curr.tiltZ))
    }
    const exceededAxes = (Object.keys(deltas) as MovementAxis[]).filter((axis) => deltas[axis] >= threshold)
    if (!exceededAxes.length) continue

    const maxDelta = Math.max(deltas.motionInX, deltas.motionInY, deltas.tiltZ)
    results.push({
      id: curr.id,
      time: curr.time,
      deltaX: deltas.motionInX,
      deltaY: deltas.motionInY,
      deltaTiltZ: deltas.tiltZ,
      maxDelta,
      exceededAxes,
      severity: classifyMovementSeverity(maxDelta)
    })
  }

  return results
}
